'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@wishket/design-system';
import { AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';

import useAskAILoadingCard from './useAskAILoadingCard';
import { AskAIResponseCard, LoadingCardContainer } from './AskAI.parts';
import {
  SlideUpContainer,
  SlideUpContainerWithDelay,
  SwipeUpContainer,
} from './MotionContainer';

import {
  useAskAIRequestAnswerMutation,
  useDeleteAskAIAnswerMutation,
  useGetAskAIAnswerQuery,
} from '@/entities/askAi/models';
import {
  AskAIGridLayout,
  AskAILoadingCard,
  AskAIQuestionCard,
  AskAIQuestionRestrictionModal,
  AskAIRecommendationCard,
  AskAIRestartCard,
  ImperativeTextAreaRef,
} from '@/features/AskAI';
import { PATH, STORAGE_KEY } from '@/shared/constants';
import { ArticleItemProps } from '@/shared/types';
import { useAuth, useDeviceCheck, useLocalStorageState } from '@/shared/hooks';
import { PageTitle, Skeleton } from '@/shared/Components';

const DEFAULT_EXAMPLE_PROMPT = [
  '웹 개발자에게 필요한 네트워크 기초 지식',
  '요즘IT 2024 상반기 개발 인기 콘텐츠',
  '요즘IT TOP 12 역주행 콘텐츠',
];
const MAX_QUESTIONS_COUNT = 3;

type AnswerState =
  | {
      questionHashKey: string;
      questionText: string;
      answerText: string;
      contentsItems: ArticleItemProps[];
    }
  | 'noContent';

const QuestionHistoryListClient = dynamic(
  () => import('./AskAI.parts').then((mod) => mod.QuestionHistoryList),
  {
    ssr: false,
    loading: () => (
      <Skeleton className="hidden h-[286px] desktop:block" grayScale={100} />
    ),
  }
);

const AskAIMainCardClient = dynamic(
  () => import('@/features/AskAI').then((mod) => mod.AskAIMainCard),
  {
    ssr: false,
    loading: () => (
      <Box className="px-5 desktop:px-0">
        <Skeleton className="h-[372px] desktop:h-[432px]" grayScale={100} />
      </Box>
    ),
  }
);

const AskAISearchBoxClient = dynamic(
  () => import('@/features/AskAI').then((mod) => mod.AskAISearchBox),
  {
    ssr: false,
    loading: () => (
      <Skeleton className="hidden h-[60px] desktop:block" grayScale={100} />
    ),
  }
);

const AskAI = () => {
  const [questionText, setQuestionText] = useState('');
  const [answerState, setAnswerState] = useState<AnswerState>();
  const [selectedHashKey, setSelectedHashKey] = useState('');
  const [isRestrictionModalOpen, setIsRestrictionModalOpen] = useState(false);
  const { isDesktop } = useDeviceCheck();

  const { data: historyAnswer, isSuccess: isHistoryAnswerQuerySuccess } =
    useGetAskAIAnswerQuery({
      questionHashKey: selectedHashKey,
    });
  const { mutate, isPending: isAsking } = useAskAIRequestAnswerMutation();
  const { mutate: deleteAnswerMutate } = useDeleteAskAIAnswerMutation();
  const { title, activateTimer, remainingTime, resetLoadingCard } =
    useAskAILoadingCard();
  const searchBoxTextareaRef = useRef<ImperativeTextAreaRef>(null);
  const { isAuth } = useAuth();
  const isGuest = !isAuth;
  const [questionCount, setQuestionCount] = useLocalStorageState(
    STORAGE_KEY.ASK_AI_GUEST_QUESTION_COUNT,
    0
  );

  const focusSearchBox = () => {
    searchBoxTextareaRef.current?.focus();
  };

  const openRestrictionModal = () => {
    setIsRestrictionModalOpen(true);
  };

  const closeRestrictionModal = () => {
    setIsRestrictionModalOpen(false);
  };

  const resetAnswerState = () => {
    setAnswerState(undefined);
  };

  const resetAskStatus = () => {
    if (isAsking) return;

    setQuestionText('');
    resetAnswerState();
    setSelectedHashKey('');
    setIsRestrictionModalOpen(false);
  };

  const requestAnswer = (value: string) => {
    if (isAsking || value.trim() === '') return;
    if (isGuest && questionCount >= MAX_QUESTIONS_COUNT) {
      openRestrictionModal();
      return;
    }

    searchBoxTextareaRef.current?.reset();
    setQuestionText(value);
    resetAnswerState();
    activateTimer();

    if (!isDesktop) {
      window.scrollTo({ top: 0 });
    }

    mutate(value, {
      onSuccess: (data) => {
        if (data === 'noContent') {
          setAnswerState('noContent');
          return;
        }

        setAnswerState({
          questionHashKey: data.questionHashKey,
          questionText: data.questionText,
          answerText: data.answerText,
          contentsItems: data.contentsItems,
        });
      },
      onError: (error) => {
        const status = error.response?.data.error.status;

        if (status === 429) {
          openRestrictionModal();
          return;
        }

        alert('새로고침 후 다시 시도해주세요.');
      },
      onSettled: () => {
        resetLoadingCard();
        isGuest && setQuestionCount(questionCount + 1);
      },
    });
  };

  const getGeneratedAnswer = (questionHashKey: string) => {
    if (isAsking) return;
    setSelectedHashKey(questionHashKey);
  };

  const deleteGeneratedAnswer = (questionHashKey: string) => {
    deleteAnswerMutate(
      { questionHashKey },
      {
        onSuccess: () => {
          if (selectedHashKey === questionHashKey) {
            resetAskStatus();
          }
        },
        onError: () => {
          alert('삭제에 실패했습니다. 다시 시도해주세요.');
        },
      }
    );
  };

  useEffect(() => {
    if (!isHistoryAnswerQuerySuccess) return;

    setAnswerState({
      questionHashKey: selectedHashKey,
      questionText: historyAnswer.questionText,
      answerText: historyAnswer.answerText,
      contentsItems: historyAnswer.contentsItems,
    });
  }, [historyAnswer, isHistoryAnswerQuerySuccess, selectedHashKey]);

  const askingOrHasAnswerState = isAsking || answerState;

  return (
    <AskAIGridLayout>
      <Box
        className="cursor-pointer pl-5 grid-in-title desktop:pl-0"
        onClick={resetAskStatus}
      >
        <PageTitle title="물어봐" isAi={true} />
      </Box>
      <Box className="mt-4 flex flex-col grid-in-content desktop:mt-0 desktop:w-[744px]">
        <AnimatePresence>
          {!askingOrHasAnswerState && (
            <SwipeUpContainer>
              <AskAIMainCardClient
                samplePromptList={DEFAULT_EXAMPLE_PROMPT}
                onClickPromptCard={requestAnswer}
              />
            </SwipeUpContainer>
          )}
        </AnimatePresence>

        <Box className="desktop:mb-10">
          <AskAISearchBoxClient
            onSearch={requestAnswer}
            isLoading={isAsking}
            ref={searchBoxTextareaRef}
          />
        </Box>

        <Box className="px-5 desktop:px-0">
          {isAsking && (
            <Box className="flex flex-col gap-10">
              <SlideUpContainer>
                <AskAIQuestionCard text={questionText} />
              </SlideUpContainer>
              <SlideUpContainerWithDelay>
                <LoadingCardContainer>
                  <AskAILoadingCard
                    title={title}
                    remainingTime={remainingTime}
                  />
                  <AskAIRecommendationCard isLoggedIn={isAuth} />
                </LoadingCardContainer>
              </SlideUpContainerWithDelay>
            </Box>
          )}

          {answerState && answerState !== 'noContent' && (
            <Box
              key={answerState.questionHashKey}
              className="flex flex-col gap-10 pb-6"
            >
              <SlideUpContainer>
                <AskAIQuestionCard text={answerState.questionText} />
              </SlideUpContainer>
              <SlideUpContainerWithDelay>
                <AskAIResponseCard
                  text={answerState.answerText}
                  contentsItems={answerState.contentsItems}
                  shareUrl={`${process.env.NEXT_PUBLIC_APP_BASE_URL}${PATH.QUESTIONS_SHARE}/${answerState.questionHashKey}`}
                  shareTitle={`${answerState.questionText} | 물어봐 AI`}
                />
              </SlideUpContainerWithDelay>
            </Box>
          )}

          {answerState && answerState === 'noContent' && (
            <LoadingCardContainer>
              <AskAIRestartCard />
              <AskAIRecommendationCard isLoggedIn={isAuth} />
            </LoadingCardContainer>
          )}
        </Box>

        <AskAIQuestionRestrictionModal
          isOpen={isRestrictionModalOpen}
          onClose={closeRestrictionModal}
        />
      </Box>
      <Box className="mr-5 justify-self-end grid-in-list desktop:mr-0 desktop:justify-self-auto">
        <QuestionHistoryListClient
          onHistoryClick={getGeneratedAnswer}
          onHistoryDelete={deleteGeneratedAnswer}
          onEmptyItemClick={focusSearchBox}
        />
      </Box>
    </AskAIGridLayout>
  );
};

export default AskAI;
